import { type AppState, useAuth0 } from "@auth0/auth0-react";
import { Spin } from "antd";
import type React from "react";
import { useCallback, useState } from "react";
import "./style.css";
import { Tracing } from "~app/tracing";

type Props = {
  children?: React.ReactNode;
  appState?: AppState | null;
};

export const AuthChecking: React.FC<Props> = ({ children, appState }: Props) => {
  const { user, loginWithRedirect, isLoading, isAuthenticated, getAccessTokenSilently, getIdTokenClaims, logout } =
    useAuth0();
  const [isNewTokenGenerated, setIsNewTokenGenerated] = useState(false);
  const [timerId, setTimerId] = useState<any>(null);

  const stopTimer = useCallback(() => {
    if (timerId) {
      clearTimeout(timerId);
    }
  }, [timerId]);

  if (isLoading)
    return (
      <div className="loading-full-screen">
        <Spin />
      </div>
    );

  if (isAuthenticated) {
    if (user) {
      Tracing.setUser(user);
    }
  } else {
    loginWithRedirect({
      appState: {
        returnTo: window.location.href,
      },
    });
    return;
  }

  const invalidateToken = async () => {
    try {
      await getAccessTokenSilently({
        cacheMode: "off",
      });

      const idToken = await getIdTokenClaims();
      if (idToken && idToken.exp !== undefined && idToken.iat !== undefined) {
        const timeMs = (idToken.exp - idToken.iat - 3600) * 1000;
        if (timeMs <= 0) {
          throw new Error("Invalid token lifetime");
        }
        setTimerId(
          setTimeout(() => {
            invalidateToken();
          }, timeMs)
        );
        setIsNewTokenGenerated(true);
      } else {
        throw new Error("Can't get token");
      }
    } catch (e) {
      console.error(e);
      stopTimer();

      await logout({
        logoutParams: {
          returnTo: `${window.location.origin}/logout?returnTo=${window.location.href}`,
        },
      });
    }
  };

  if (!isNewTokenGenerated) {
    stopTimer();
    if (!appState) {
      invalidateToken();
    }
    return (
      <div className="loading-full-screen">
        <Spin />
      </div>
    );
  } else {
    return children;
  }
};
